import { Controller } from '@hotwired/stimulus';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static values = {
        online: Boolean,
        saveUrl: String,
        reactivateUrl: String,
    };

    static targets = [
        'bannerOnline',
        'bannerOffline',
        'phoneDisplay',
        'phoneValue',
        'phoneEdit',
        'phoneInput',
        'phoneError',
        'suspendedBanner',
    ];

    // The banner link does exactly what the header toggle does, so it goes through
    // the toggle: going online still opens the confirmation pop-up, and the header,
    // the banner and the saved status all move together.
    toggleStatus(event) {
        event.preventDefault();

        const toggle = document.getElementById('online-offline-switch');

        if (!toggle) {
            return;
        }

        toggle.checked = !this.onlineValue;
        toggle.dataset.popupSource = 'page_banner';
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Fired by app.js whenever the header toggle changes state.
    statusChanged(event) {
        this.onlineValue = event.detail.isOnline;

        // Going online by hand lifts the schedule suspension (server side too).
        if (event.detail.isOnline) {
            this.scheduleResumed();
        }
    }

    // « Réactiver mes horaires » in the suspended banner.
    async reactivateSchedule(event) {
        event.preventDefault();

        showPreloader();

        try {
            const response = await fetch(this.reactivateUrlValue, {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            const result = await response.json();

            if (!result.status) {
                addAlert('danger', translations['profile.An unknown error occurred.']);

                return;
            }

            dataLayer.push({
                event: 'dataLayer.gaEvent',
                gaEvent: { category: 'Account', action: 'onlineconsult_schedule_resume', label: '' },
            });

            const popupSummary = document.getElementById('online-consultation-schedule-summary');

            if (popupSummary) {
                popupSummary.innerHTML = result.summary_html;
            }

            document.dispatchEvent(new CustomEvent('online-consultation:schedule-resumed'));
        } catch (error) {
            addAlert('danger', translations['profile.An unknown error occurred.']);
        } finally {
            hidePreloader();
        }
    }

    // online-consultation:schedule-resumed@document: from here, the pop-up or a schedule save.
    scheduleResumed() {
        this.suspendedBannerTarget.classList.replace('d-flex', 'd-none');
    }

    onlineValueChanged(isOnline) {
        this.bannerOnlineTarget.classList.toggle('d-flex', isOnline);
        this.bannerOnlineTarget.classList.toggle('d-none', !isOnline);
        this.bannerOfflineTarget.classList.toggle('d-flex', !isOnline);
        this.bannerOfflineTarget.classList.toggle('d-none', isOnline);
    }

    editPhone() {
        this.phoneInputTarget.value = this.phoneValueTarget.textContent.trim();
        this.hidePhoneError();
        this.phoneDisplayTarget.classList.replace('d-flex', 'd-none');
        this.phoneEditTarget.classList.remove('d-none');
        this.phoneInputTarget.focus();
    }

    cancelPhone() {
        this.hidePhoneError();
        this.phoneEditTarget.classList.add('d-none');
        this.phoneDisplayTarget.classList.replace('d-none', 'd-flex');
    }

    async savePhone(event) {
        event.preventDefault();

        const phone = this.phoneInputTarget.value.trim();

        // An empty value would delete the number, and the page would then have
        // nothing to show, so a number is required here.
        if ('' === phone) {
            this.showPhoneError(translations['profile.Invalid phone number.']);
            this.pushPhoneEvent('invalid');

            return;
        }

        showPreloader();

        try {
            const response = await fetch(this.saveUrlValue, {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                body: new URLSearchParams({
                    type: 'contacts',
                    onlineConsultationPhoneNumber: phone,
                }),
            });
            const result = await response.json();

            if (!result.status) {
                this.showPhoneError(result.message || translations['profile.An unknown error occurred.']);

                // A 400 is the server-side number check; anything else is not about the number.
                if (400 === response.status) {
                    this.pushPhoneEvent('invalid');
                }

                return;
            }

            this.phoneValueTarget.textContent = phone;

            // The go-online pop-up recaps the same number: keep it in step.
            const popupPhone = document.getElementById('online-consultation-phone-summary');

            if (popupPhone) {
                popupPhone.textContent = phone;
            }

            this.cancelPhone();
            addAlert('success', translations['profile.Changes saved!']);
            this.pushPhoneEvent('success');
        } catch (error) {
            this.showPhoneError(translations['profile.An unknown error occurred.']);
        } finally {
            hidePreloader();
        }
    }

    pushPhoneEvent(label) {
        dataLayer.push({
            event: 'dataLayer.gaEvent',
            gaEvent: { category: 'Account', action: 'onlineconsult_phone_update', label },
        });
    }

    showPhoneError(message) {
        this.phoneInputTarget.classList.add('is-invalid');
        this.phoneErrorTarget.textContent = message;
        this.phoneErrorTarget.classList.remove('d-none');
    }

    hidePhoneError() {
        this.phoneInputTarget.classList.remove('is-invalid');
        this.phoneErrorTarget.classList.add('d-none');
    }
}
